import React, { useState } from "react";
import {
  Flex,
  Button,
  Text,
  Textarea,
  Stack,
  IconButton,
  Link,
  useToast,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import * as Sentry from "@sentry/react";

import MoblyzeApolloClient from "../../services/MoblyzeApolloClient";

import {
  AGENCY_CANDIDATE_TRIAGE_ADD_TO_SHORTLIST_MUTATION,
  AGENCY_CANDIDATE_TRIAGE_REMOVE_FROM_SHORTLIST_MUTATION,
  AGENCY_CANDIDATE_TRIAGE_ADD_TO_PASS_LIST_MUTATION,
  AGENCY_CANDIDATE_TRIAGE_MARK_AS_HIRED_MUTATION,
  AGENCY_CANDIDATE_TRIAGE_SEND_INVITE_MUTATION,
  AGENCY_CANDIDATE_TRIAGE_RECOMMEND_MUTATION,
  AGENCY_CANDIDATE_TRIAGE_UNRECOMMEND_MUTATION,
} from "../../graphql/jobs";
import FormModal from "../FormModal";
import colors from "../../helpers/colors";
import { AGENCY_TOGGLE_FAVOURITE_CANDIDATE_MUTATION } from "../../graphql/agencies";
import { FaBookmark, FaRegBookmark, FaRegStar } from "react-icons/fa";
import { MdOutlineMobileScreenShare } from "react-icons/md";

const onAddToShortlist = ({ toast, moblyzeJob, candidate, refetch }) => {
  MoblyzeApolloClient.mutate({
    mutation: AGENCY_CANDIDATE_TRIAGE_ADD_TO_SHORTLIST_MUTATION,
    variables: {
      moblyzeJobId: moblyzeJob.id,
      candidateProfileId: candidate.candidateProfileId,
    },
  })
    .then((result) => {
      if (refetch) {
        refetch();
      }
      toast({
        title: "Candidate added to shortlist",
        status: "success",
      });
    })
    .catch((e) => {
      Sentry.captureException(e);

      toast({
        title: "An error occurred",
        description: e.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    });
};

const onInvite = ({ toast, moblyzeJob, candidate, refetch }) => {
  MoblyzeApolloClient.mutate({
    mutation: AGENCY_CANDIDATE_TRIAGE_SEND_INVITE_MUTATION,
    variables: {
      moblyzeJobId: moblyzeJob.id,
      candidateProfileId: candidate.candidateProfileId,
    },
  })
    .then((result) => {
      if (refetch) {
        refetch();
      }
      toast({
        title: "Candidate was invited",
        status: "success",
      });
    })
    .catch((e) => {
      Sentry.captureException(e);

      toast({
        title: "An error occurred",
        description: e.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    });
};

export const AgencyOnFavourite = ({ toast, candidateReport, refetch }) => {
  MoblyzeApolloClient.mutate({
    mutation: AGENCY_TOGGLE_FAVOURITE_CANDIDATE_MUTATION,
    variables: {
      candidateProfileId: candidateReport.candidate.candidateProfileId,
    },
  })
    .then((result) => {
      if (refetch) {
        refetch();
      }
      const doneText = candidateReport.favourite ? "removed from" : "added to";
      toast({
        title: candidateReport.candidate.fullName + ` has been ${doneText} your favourites`,
        status: "success",
      });
    })
    .catch((e) => {
      Sentry.captureException(e);

      toast({
        title: "An error occurred",
        description: e.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    });
};

// hideAddToShortlist suppresses the "Add to Shortlist" button while keeping
// "Pass". The caller passes this for external (pending sign-up) candidates
// while the external_candidate_shortlist flag is off: shortlisting them is
// API-gated off until the flag flips on. Pass is still allowed.
export function AgencyPassAddActions({ candidate, moblyzeJob, refetch, hideAddToShortlist = false }) {
  const toast = useToast();

  const onPass = () => {
    MoblyzeApolloClient.mutate({
      mutation: AGENCY_CANDIDATE_TRIAGE_ADD_TO_PASS_LIST_MUTATION,
      variables: {
        moblyzeJobId: moblyzeJob.id,
        candidateProfileId: candidate.candidateProfileId,
      },
    })
      .then((result) => {
        if (refetch) {
          refetch();
        }
        toast({
          title: "Passed on candidate",
          status: "success",
        });
      })
      .catch((e) => {
        Sentry.captureException(e);

        toast({
          title: "An error occurred",
          description: e.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };

  return (
    <Flex gap="12px">
      <Button variant="outline" colorScheme="red" size="sm" onClick={onPass}>
        Pass
      </Button>
      {!hideAddToShortlist && (
        <Button
          variant="outline"
          colorScheme="green"
          size="sm"
          onClick={() =>
            onAddToShortlist({ toast, moblyzeJob, candidate, refetch })
          }
        >
          Add to Shortlist
        </Button>
      )}
    </Flex>
  );
}

export function AgencyRemoveShortlistAction({
  candidate,
  moblyzeJob,
  refetch,
}) {
  const toast = useToast();

  const onRemoveFromShortlist = () => {
    MoblyzeApolloClient.mutate({
      mutation: AGENCY_CANDIDATE_TRIAGE_REMOVE_FROM_SHORTLIST_MUTATION,
      variables: {
        moblyzeJobId: moblyzeJob.id,
        candidateProfileId: candidate.candidateProfileId,
      },
    })
      .then((result) => {
        if (refetch) {
          refetch();
        }
        toast({
          title: "Candidate removed from shortlist",
          status: "success",
        });
      })
      .catch((e) => {
        Sentry.captureException(e);

        toast({
          title: "An error occurred",
          description: e.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };
  return (
    <Flex gap="24px">
      <Button
        variant="outline"
        colorScheme="red"
        size="sm"
        onClick={onRemoveFromShortlist}
      >
        Remove from Shortlist
      </Button>
    </Flex>
  );
}

export function AgencyMarkHiredAction({ candidate, moblyzeJob, refetch }) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const onMarkAsHired = () => {
    MoblyzeApolloClient.mutate({
      mutation: AGENCY_CANDIDATE_TRIAGE_MARK_AS_HIRED_MUTATION,
      variables: {
        moblyzeJobId: moblyzeJob.id,
        candidateProfileId: candidate.candidateProfileId,
      },
    })
      .then((result) => {
        if (refetch) {
          refetch();
        }
        toast({
          title: "Candidate marked as hired",
          status: "success",
        });
      })
      .catch((e) => {
        Sentry.captureException(e);

        toast({
          title: "An error occurred",
          description: e.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };

  return (
    <Flex gap="24px">
      <Button variant="outline" colorScheme="green" size="sm" onClick={onOpen}>
        Mark as Hired
      </Button>

      <FormModal
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        title="Are you sure you want to mark this candidate as hired?"
        onSubmit={onMarkAsHired}
        submitTitle="Yes"
      />
    </Flex>
  );
}

export function AgencyAddToShortlistAction({ candidate, moblyzeJob, refetch }) {
  const toast = useToast();

  return (
    <Flex gap="24px">
      <Button
        variant="outline"
        colorScheme="green"
        size="sm"
        onClick={() =>
          onAddToShortlist({ toast, moblyzeJob, candidate, refetch })
        }
      >
        Add to Shortlist
      </Button>
    </Flex>
  );
}

export function AgencyInviteAction({ candidate, moblyzeJob, refetch }) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (!candidate || !moblyzeJob) {
    return null;
  }

  const isPending = !!candidate.isExternal;
  const phone = candidate.primaryPhone;
  const email = candidate.email;

  return (
    <Flex gap="24px">
      <Button
        variant="outline"
        colorScheme="white"
        size="sm"
        onClick={onOpen}
        rightIcon={<MdOutlineMobileScreenShare/>}
      >
        Invite to job
      </Button>

      <FormModal
        dark={true}
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        title={`Ready to invite ${candidate.fullName}?`}
        onSubmit={() =>
          onInvite({ toast, moblyzeJob, candidate, refetch })
        }
        submitTitle="Send Invite"
      >
        {isPending ? (
          <Stack>
            <Text fontSize="sm">
              {candidate.fullName} has not signed in to the Moblyze app yet. They will see this job at the top of their list once they do.
            </Text>
            {(phone || email) && (
              <Text fontSize="sm">
                {phone && (
                  <>
                    Call them on{" "}
                    <Link href={`tel:${phone}`} color="teal.300">{phone}</Link>
                  </>
                )}
                {phone && email && " or "}
                {email && (
                  <>
                    Email them at{" "}
                    <Link href={`mailto:${email}`} color="teal.300">{email}</Link>
                  </>
                )}
                {" "}to encourage them to sign up and apply.
              </Text>
            )}
          </Stack>
        ) : (
          <Stack>
            <Text fontSize="sm">
              You're about to invite {candidate.fullName} to apply for the {moblyzeJob.category} position. Invited jobs appear first in the candidate’s feed.
            </Text>
            <Text fontSize="sm">
              Would you like to go ahead?
            </Text>
          </Stack>
        )}
      </FormModal>
    </Flex>
  );
}

const runTriageMutation = ({ toast, mutation, variables, refetch, successTitle }) =>
  MoblyzeApolloClient.mutate({ mutation, variables })
    .then(() => {
      if (refetch) {
        refetch();
      }
      toast({
        title: successTitle,
        status: "success",
      });
    })
    .catch((e) => {
      Sentry.captureException(e);

      toast({
        title: "An error occurred",
        description: e.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    });

// Recommend actions on the Suggested list. A candidate who isn't recommended
// gets "Recommend" (a modal with an optional reason); a recommended one gets
// "Edit reason" (the same modal, prefilled; recommending again updates the
// reason) and "Remove recommendation". Not gated on the external-candidate
// flags: recommending is recruiter-side curation the API allows for external
// (pending sign-up) candidates too.
export function AgencyRecommendAction({ candidateReport, moblyzeJob, refetch }) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [reason, setReason] = useState("");

  const candidate = candidateReport?.candidate;
  if (!candidate || !moblyzeJob) {
    return null;
  }

  const recommended = !!candidateReport.recommended;
  const variables = {
    moblyzeJobId: moblyzeJob.id,
    candidateProfileId: candidate.candidateProfileId,
  };

  const openModal = () => {
    setReason(recommended ? candidateReport.recommendedReason || "" : "");
    onOpen();
  };

  const onRecommend = () => {
    onClose();
    runTriageMutation({
      toast,
      mutation: AGENCY_CANDIDATE_TRIAGE_RECOMMEND_MUTATION,
      variables: { ...variables, reason: reason.trim() || null },
      refetch,
      successTitle: recommended ? "Recommendation updated" : "Candidate recommended",
    });
  };

  const onUnrecommend = () => {
    runTriageMutation({
      toast,
      mutation: AGENCY_CANDIDATE_TRIAGE_UNRECOMMEND_MUTATION,
      variables,
      refetch,
      successTitle: "Recommendation removed",
    });
  };

  return (
    <Flex gap="12px">
      {recommended ? (
        <>
          <Button variant="outline" colorScheme="yellow" size="sm" onClick={openModal}>
            Edit reason
          </Button>
          <Button variant="outline" colorScheme="red" size="sm" onClick={onUnrecommend}>
            Remove recommendation
          </Button>
        </>
      ) : (
        <Button
          variant="outline"
          colorScheme="yellow"
          size="sm"
          leftIcon={<FaRegStar />}
          onClick={openModal}
        >
          Recommend
        </Button>
      )}

      <FormModal
        dark={true}
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        title={
          recommended
            ? `Edit why you recommend ${candidate.fullName}`
            : `Recommend ${candidate.fullName}?`
        }
        onSubmit={onRecommend}
        submitTitle={recommended ? "Save" : "Recommend"}
      >
        <Stack>
          <Text fontSize="sm" color="white">
            Recommended candidates are starred at the top of the Suggested list.
          </Text>
          <Textarea
            aria-label="Reason (optional)"
            placeholder="Why this candidate? (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            // The theme gives textareas a white background; match the client
            // note box (ClientShortlistActions) so the reason is readable.
            color={colors.charcoal}
            rows={4}
          />
        </Stack>
      </FormModal>
    </Flex>
  );
}

export function AgencyFavouriteAction({ candidateReport, refetch }) {
  const toast = useToast();
  const favourite = candidateReport?.favourite;

  return (
    <Flex gap="24px">
      <IconButton
        variant={favourite ? "" : "outline"}
        colorScheme={favourite ? "green.400" : "white"}
        size="sm"
        icon={favourite ? <FaBookmark/> : <FaRegBookmark/>}
        bgColor={favourite ? "green.800" : "transparent"}
        textColor={favourite ? "green.400" : "white"}
        onClick={() =>
          AgencyOnFavourite({ toast, candidateReport, refetch })
        }
      >
      </IconButton>
    </Flex>
  );
}

export function AgencyChangeStatusMenu({ candidate, moblyzeJob, refetch }) {
  const toast = useToast();

  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        variant="ghost"
        color="whiteAlpha.600"
        _hover={{ color: "white", bg: "whiteAlpha.100" }}
        _active={{ bg: "whiteAlpha.200" }}
        rightIcon={<ChevronDownIcon />}
      >
        Change status
      </MenuButton>
      <MenuList bg="gray.800" borderColor="whiteAlpha.200">
        <MenuItem
          bg="gray.800"
          _hover={{ bg: "gray.700" }}
          color="white"
          onClick={() => onAddToShortlist({ toast, moblyzeJob, candidate, refetch })}
        >
          Skip to Shortlisted
        </MenuItem>
      </MenuList>
    </Menu>
  );
}
